import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableStructure from "../../CommonComponents/TableStructure";
import { getAllDocTypesData, deleteDocTypeSingle, deleteDocTypesBulk } from "../../services/DocumentTypes";
import { setPreview } from "../../redux/actions/previewActions";
import { Box, Image, useToast } from "@chakra-ui/react";
import { showErrorToast, showSuccessToast } from "../../util/errorHandler";
import { getImageURL } from "../../util/getImage";
const IMAGE_BASE_PATH = process.env.REACT_APP_DEV_IMAGE_BASE_PATH;
const placeholderImg = `${IMAGE_BASE_PATH}/ui/DummyImage/small.png`;

const DocTypesTable = () => {
  const [records, setRecords] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting state
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  const session = useSelector((state) => state.session);
  const dispatch = useDispatch();
  const toast = useToast();
  const limit = 50; // Number of records per page

  // Fetch projects from the API
  const fetchProjects = async (page, term) => {
    setLoading(true);
    try {
      const result = await getAllDocTypesData(limit, page, term);
      if (result?.data) {
        setRecords(citationFactor(result?.data?.data || []));
        setTotalPages(Math.ceil(result?.data?.total / limit));
        setTotalRecords(result?.data?.total);

        if (result.data.page_number && parseInt(result.data.page_number) !== currentPage) {
          setCurrentPage(parseInt(result.data.page_number));
        }
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };


  const citationFactor = (data) => {
    return data?.map((item) => {
      return {
        _id: item?._id || item?.id,
        name: item?.name,
        alias: item?.alias,
        classification: item?.classification,
        doc_type: item?.doc_type,
        cdt_image: item?.cdt_image,
      }
    })
  }

  const columns = [
    {
      label: "Document Type Details",
      accessor: "name",
    },
    {
      label: "Alias",
      accessor: "alias",
    },
    {
      label: "Classification",
      accessor: "classification",
    },
    {
      label: "Document Type",
      accessor: "doc_type",
    },
    {
      label: "Image",
      accessor: "cdt_image",
      render: (value) => (
        <Image
          src={getImageURL(value, "200", "100")}
          width="100px"
          height="60px"
          objectFit="contain"
          fallbackSrc={placeholderImg}
          alt="Document type image"
        />
      ),
    },
  ]

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false; // Skip the first render
      setCurrentPage(1); // Reset to page 1 for a new search
      setSearchText(searchTerm);
      fetchProjects(1, searchTerm);
    }
  };

  const sortRecords = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc"; // Update sortOrder first
    setSortOrder(newSortOrder);

    const sorted = [...records].sort((a, b) =>
      newSortOrder === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );

    setRecords(sorted);
  };

  const isInitialLoad = useRef(true);

  useEffect(() => {
    // if (isInitialLoad.current) {
    //   isInitialLoad.current = false; // Skip the first render
    //   return;
    // }

    fetchProjects(currentPage, searchText);
    dispatch(setPreview(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleClear = () => {
    setSearchText('');
    fetchProjects(1, "");
  };

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  const getRowKey = (record) => record?._id;

  const handleRowSelect = (key, checked) => {
    setSelectedIds((prev) => {
      if (checked) {
        if (prev.includes(key)) return prev;
        return [...prev, key];
      } else {
        return prev.filter((k) => k !== key);
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allKeys = records.map((r) => r?._id).filter(Boolean);
      setSelectedIds(allKeys);
    } else {
      setSelectedIds([]);
    }
  };

  const performDelete = async (record, idsToDelete) => {
    const isSingle = !!record;
    const count = isSingle ? 1 : idsToDelete.length;
    const confirmed = window.confirm(`Delete ${count} record(s)?`);
    if (!confirmed) return;
    setLoading(true);
    try {
      if (isSingle) {
        await deleteDocTypeSingle(record.alias);
      } else {
        await deleteDocTypesBulk(idsToDelete);
      }
      setSelectedIds([]);
      await fetchProjects(currentPage, searchText);
      showSuccessToast(toast, `Deleted ${count} record(s)`);
    } catch (error) {
      console.error("Error deleting document type(s):", error);
      showErrorToast(toast, "Error deleting record(s)");
    } finally {
      setLoading(false);
    }
  };

  const handleRowDelete = (_key, record) => {
    performDelete(record, null);
  };

  const handleDeleteSelected = () => {
    performDelete(null, selectedIds);
  };

  return (
    <Box px={2}>
    <TableStructure
      isAuthenticated={session?.isAuthenticated}
      handleSearch={handleSearch}
      handleClear={handleClear}
      handlePageChange={handlePageChange}
      currentPage={currentPage}
      totalPages={totalPages}
      sortRecords={sortRecords}
      totalRecords={totalRecords}
      sortOrder={sortOrder}
      loading={loading}
      records={records}
      type="document-types"
      title="Document Types"
      addOption
      columns={columns}
      selectable
      selectedRowKeys={selectedIds}
      getRowKey={getRowKey}
      onRowSelect={handleRowSelect}
      onSelectAll={handleSelectAll}
      onRowDelete={handleRowDelete}
      onDeleteSelected={handleDeleteSelected}
    />
    </Box>
  );
};

export default DocTypesTable;
